import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useRef } from 'react';
import { resetRefreshState } from '@/Config/api/httpClient.config';
import { clearToken } from '@/Config/auth/authStore';
import { getSafeNextPath } from '@/Domain/utils/safeNextPath';

export const useLogout = () => {
  const posthog = usePostHog();
  const returnToRef = useRef<string | null>(null);

  const mutation = useMutation({
    mutationKey: ['accounts/logout'],
    onSuccess: () => {
      // Tear down auth state BEFORE anything that can throw (posthog.reset) so
      // the in-memory token and any in-flight refresh never outlive the session
      // even if the subsequent full-page reload is delayed or interrupted.
      clearToken();
      resetRefreshState();
      posthog.reset();
      const returnTo = returnToRef.current;
      if (returnTo) {
        window.location.href = `/?${new URLSearchParams({ next: returnTo }).toString()}`;
        return;
      }
      window.location.pathname = '/';
    },
  });

  // `returnTo` is a same-origin path to resume once the user signs in again. It rides to the
  // login page as `?next=`, which useLogin and useSocialLogin already replay.
  const handleLogout = (returnTo?: string) => {
    // Two call sites already passed this hook straight to `onClick`, and nothing type-checks in CI.
    returnToRef.current = typeof returnTo === 'string' ? (getSafeNextPath(returnTo) ?? null) : null;
    mutation.mutate();
  };

  // isSuccess keeps "logging out" true through the post-success navigation (no flicker).
  return { handleLogout, isLoggingOut: mutation.isPending || mutation.isSuccess };
};
